import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { claims as claimsApi, finance } from '../../services/api';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import ClaimDetailModal from '../../components/common/ClaimDetailModal';
import { theme } from '../../theme';

function MyClaims() {
  const navigate = useNavigate(); // used for New Claim button
  const [claimsData, setClaimsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewClaim, setViewClaim] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportDownloading, setReportDownloading] = useState(false);

  const handleDownloadReport = async () => {
    setReportDownloading(true);
    try {
      const res = await finance.downloadMonthlyClaimsReport({ month: reportMonth, year: reportYear });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Claims_Report_${reportYear}_${String(reportMonth).padStart(2, '0')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to generate report.');
    } finally { setReportDownloading(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await claimsApi.getMyClaims();
      const d = r.data;
      setClaimsData(Array.isArray(d) ? d : d.items || []);
    } catch { setClaimsData([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openView = async (id) => {
    setViewLoading(true);
    try {
      const r = await claimsApi.getClaim(id);
      setViewClaim(r.data);
    } catch { alert('Failed to load claim details.'); }
    finally { setViewLoading(false); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this claim? This cannot be undone.')) return;
    try {
      await claimsApi.cancelClaim(id);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to cancel claim.');
    }
  };

  const canEdit = (status) => ['submitted', 'under_review'].includes(status);
  const isDraft = (status) => status === 'draft';
  const isRejected = (status) => status === 'rejected';

  const columns = [
    { header: 'Claim #', key: 'claim_code', render: (v, row) => v || `#${row.id}` },
    {
      header: 'Title', key: 'title',
      render: (v, row) => (
        <div>
          <span>{v}</span>
          {isRejected(row.status) && row.finance_remarks && (
            <div style={{ marginTop: '0.3rem', padding: '0.3rem 0.6rem', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.375rem', fontSize: '0.72rem', color: '#DC2626', display: 'flex', gap: '0.35rem', alignItems: 'flex-start' }}>
              <span style={{ fontWeight: 700, flexShrink: 0 }}>Rejection reason:</span>
              <span>{row.finance_remarks}</span>
            </div>
          )}
        </div>
      ),
    },
    { header: 'Project', key: 'project_name', render: (v) => v || '—' },
    { header: 'Total Amount', key: 'total_amount', render: (v) => `₹${Number(v || 0).toLocaleString()}` },
    {
      header: 'Approved Amount', key: 'total_amount',
      render: (_, row) => (row.status === 'approved' || row.status === 'paid')
        ? <span style={{ color: theme.success, fontWeight: 600 }}>₹{Number(row.total_amount || 0).toLocaleString()}</span>
        : '—',
    },
    { header: 'Submitted', key: 'submitted_at', render: (v) => v ? new Date(v).toLocaleDateString() : '—' },
    { header: 'Status', key: 'status', render: (v, row) => <Badge status={v || 'submitted'} byName={row.reviewed_by_name} /> },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {!isDraft(row.status) && (
            <Button size="sm" variant="outline" onClick={() => openView(id)}>View</Button>
          )}
          {isDraft(row.status) && (
            <Button size="sm" variant="primary" onClick={() => navigate(`/employee/claims/${id}/edit`)}>Continue Draft →</Button>
          )}
          {canEdit(row.status) && (
            <Button size="sm" variant="outline" onClick={() => navigate(`/employee/claims/${id}/edit`)}>Edit</Button>
          )}
          {isRejected(row.status) && (
            <Button size="sm" variant="warning" onClick={() => navigate(`/employee/claims/${id}/edit`)}>Edit & Resubmit</Button>
          )}
          {(canEdit(row.status) || isDraft(row.status)) && (
            <Button size="sm" variant="danger" onClick={() => handleCancel(id)}>Cancel</Button>
          )}
        </div>
      ),
    },
  ];

  const totalPending = claimsData.filter((c) => c.status === 'submitted' || c.status === 'under_review').reduce((sum, c) => sum + Number(c.total_amount || 0), 0);
  const totalApproved = claimsData.filter((c) => c.status === 'paid').reduce((sum, c) => sum + Number(c.total_amount || 0), 0);
  const totalPendingPayment = claimsData.filter((c) => c.status === 'approved').reduce((sum, c) => sum + Number(c.total_amount || 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>My Claims</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{claimsData.length} total claims</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Monthly Report Download */}
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', background: theme.pageBg, padding: '0.4rem 0.75rem', borderRadius: '0.5rem', border: `1px solid ${theme.border}` }}>
            <span style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: 600 }}>Report:</span>
            <select value={reportMonth} onChange={e => setReportMonth(Number(e.target.value))}
              style={{ padding: '0.3rem 0.5rem', border: `1px solid ${theme.border}`, borderRadius: '0.375rem', fontSize: '0.78rem', outline: 'none' }}>
              {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
            <select value={reportYear} onChange={e => setReportYear(Number(e.target.value))}
              style={{ padding: '0.3rem 0.5rem', border: `1px solid ${theme.border}`, borderRadius: '0.375rem', fontSize: '0.78rem', outline: 'none' }}>
              {Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Button size="sm" variant="outline" loading={reportDownloading} onClick={handleDownloadReport}>
              ⬇ Download Report
            </Button>
          </div>
          <Button variant="primary" onClick={() => navigate('/employee/claims/new')}>+ Submit New Claim</Button>
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        <div style={{ background: theme.white, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${theme.border}`, borderLeft: `4px solid ${theme.warning}` }}>
          <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0 0 0.25rem', fontWeight: 600, textTransform: 'uppercase' }}>Pending Review</p>
          <p style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.text, margin: 0 }}>₹{totalPending.toLocaleString()}</p>
        </div>
        <div style={{ background: theme.white, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${theme.border}`, borderLeft: `4px solid ${theme.primary}` }}>
          <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0 0 0.25rem', fontWeight: 600, textTransform: 'uppercase' }}>Pending Payment</p>
          <p style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.primary, margin: 0 }}>₹{totalPendingPayment.toLocaleString()}</p>
          <p style={{ fontSize: '0.7rem', color: theme.textMuted, margin: '0.15rem 0 0' }}>Approved, awaiting payout</p>
        </div>
        <div style={{ background: theme.white, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${theme.border}`, borderLeft: `4px solid ${theme.success}` }}>
          <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0 0 0.25rem', fontWeight: 600, textTransform: 'uppercase' }}>Total Paid</p>
          <p style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.success, margin: 0 }}>₹{totalApproved.toLocaleString()}</p>
        </div>
        <div style={{ background: theme.white, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${theme.border}`, borderLeft: `4px solid ${theme.info}` }}>
          <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0 0 0.25rem', fontWeight: 600, textTransform: 'uppercase' }}>Total Claims</p>
          <p style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.text, margin: 0 }}>{claimsData.length}</p>
        </div>
      </div>

      <Card padding="0">
        <Table columns={columns} data={claimsData} loading={loading || viewLoading} emptyMessage="No claims submitted yet" />
      </Card>

      <ClaimDetailModal claim={viewClaim} onClose={() => setViewClaim(null)} />
    </div>
  );
}

export default MyClaims;
